// Client for process mining (apps/api/src/mining/routes.ts).
import { request } from "../api";

export type ProcessInfo = { id: string; name: string; noun: string; plural: string; area: string };
export type Suggestion = { headline: string; why: string; steps: string[]; type: string; effort: "S" | "M" | "L"; ai?: string };
export type Metric = { label: string; value: number; unit: string; better: "lower" | "higher" };
export type Insight = {
  id: string; key: string; process: ProcessInfo; kind: string; title: string; finding: string; evidence: any; metric: Metric; baseline: Metric | null;
  change: { from: number; to: number; better: boolean | null } | null; impactHours: number | null; score: number; suggestion: Suggestion | null; suggestionBy: string | null;
  status: "new" | "accepted" | "dismissed"; live: boolean; issue: { number: string; state: string } | null; decidedBy: string | null; decidedAt: string | null; reason: string | null; firstSeen: string; lastSeen: string;
};
export type Overview = {
  processes: (ProcessInfo & { cases: number; events: number; lastAt: string; insights: number })[];
  insights: { open: number; hours: number; accepted: number; screens: number }; screens: { events: number; people: number; days: number };
  capture: boolean; model: "anthropic" | "gemini" | null; lastRun: string;
};
export type PEdge = { from: string; to: string; count: number; cases: number; medianMs: number; p80Ms: number; totalMs: number; handoffs: number; handoffMedianMs: number | null; sameMedianMs: number | null; back: boolean };
export type PNode = { id: string; cases: number; events: number; pos: number; outgoing: number; incoming: number; repeats: number };
export type PVariant = { key: string; steps: string[]; count: number; pct: number; medianMs: number | null; done: number };
export type PCase = { id: string; ref: string | null; start: string; end: string; done: boolean; durationMs: number; idleMs: number; last: string; rework: boolean; v: number; steps?: string[] };
export type Kpis = { cases: number; done: number; open: number; events: number; medianMs: number | null; p80Ms: number | null; straightPct: number | null; reworkPct: number | null; handoffsPerCase: number | null; variants: number; perMonth: number };
export type ProcessView = {
  process: ProcessInfo; days: number; terminal: string[]; kpis: Kpis; nodes: PNode[]; edges: PEdge[]; starts: { to: string; count: number }[]; ends: { from: string; count: number }[];
  variants: PVariant[]; cases: PCase[]; insights: Insight[];
};
export type ScreenRow = { path: string; name: string; visits: number; people: number; minutes: number; medianSec: number; next: { path: string; name: string; count: number }[]; bc: { page: string; name: string; count: number }[] };
export type ActionRow = { path: string; name: string; count: number; fails: number; people: number; medianMs: number };
export type ScreensView = { days: number; people: number; events: number; screens: ScreenRow[]; actions: ActionRow[]; insights: Insight[]; capture: boolean };

const B = "/api/process-mining";
export const pm = {
  overview: () => request<Overview>("GET", `${B}/overview`),
  process: (id: string, days: number, minor: boolean) => request<ProcessView>("GET", `${B}/process/${encodeURIComponent(id)}?days=${days}${minor ? "&minor=1" : ""}`),
  screens: (days = 30) => request<ScreensView>("GET", `${B}/screens?days=${days}`),
  insights: (status: "open" | "accepted" | "dismissed" | "gone" | "all", process?: string) => request<Insight[]>("GET", `${B}/insights?status=${status}${process ? `&process=${encodeURIComponent(process)}` : ""}`),
  write: (ids?: string[]) => request<{ written: number; model: string | null }>("POST", `${B}/insights/write`, ids ? { ids } : {}),
  accept: (id: string) => request<Insight>("POST", `${B}/insights/${id}/accept`, {}),
  dismiss: (id: string, reason: string) => request<Insight>("POST", `${B}/insights/${id}/dismiss`, { reason }),
  reopen: (id: string) => request<Insight>("POST", `${B}/insights/${id}/reopen`),
  refresh: () => request<unknown>("POST", `${B}/refresh`),
  setCapture: (capture: boolean) => request<{ capture: boolean }>("PUT", `${B}/settings`, { capture }),
};

const H = 36e5, DAY = 864e5;
/** "45 min", "6.5 h", "3.2 days". */
export function dur(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !isFinite(ms)) return "–";
  if (ms < H) return `${Math.max(1, Math.round(ms / 60_000))} min`;
  if (ms < 2 * DAY) { const h = ms / H; return `${h < 10 ? Math.round(h * 10) / 10 : Math.round(h)} h`; }
  const d = ms / DAY; return `${d < 10 ? Math.round(d * 10) / 10 : Math.round(d)} days`;
}
/** Short form for labels on the map: "45m", "6.5h", "3.2d". */
export function durShort(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !isFinite(ms)) return "–";
  if (ms < H) return `${Math.max(1, Math.round(ms / 60_000))}m`;
  if (ms < 2 * DAY) { const h = ms / H; return `${h < 10 ? Math.round(h * 10) / 10 : Math.round(h)}h`; }
  const d = ms / DAY; return `${d < 10 ? Math.round(d * 10) / 10 : Math.round(d)}d`;
}

export const KIND_LABEL: Record<string, string> = {
  bottleneck: "Bottleneck", rework: "Rework", handoff: "Handoff", stuck: "Stalled", automate: "Automate", variants: "No standard path",
  pingpong: "Screen", swivel: "Business Central", failing: "Rejected action", repetitive: "One at a time",
};

/** Where a record in a process opens in courageMFG. */
export function recordHref(process: string, ref: string): string {
  if (!ref) return "#";
  if (process === "issue" || process === "capa") return `/r/${ref}`;
  if (process === "work_order") return `/maintenance/wo/${ref}`;
  if (process === "asset") return `/maintenance/assets/${ref}`;
  if (process === "pm") return `/maintenance/pm/${ref}`;
  if (process === "opportunity") return `/opportunities/${ref}`;
  if (process === "quote") return `/quotes/${ref}`;
  if (process === "ticket") return `/service-desk/${ref}`;
  if (process === "po_confirmation") return `/supplier-portal/po/${ref}`;
  if (process.startsWith("app:")) return `/m/${process.slice(4)}/${ref}`;
  return `/search?q=${encodeURIComponent(ref)}`;
}
